import { useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  EmptySectionCard,
  FAIconName,
  FireflyButton,
  Icon,
  MaudCardFrame,
  MaudSectionHeader,
  Text,
} from '@equip.health/ui';

import { PatientEvents } from '~/analytics/patientEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import {
  getPatientAppSettingReadModeTextWithName,
  getPreferredFirstName,
} from '~/lib/util/patient.util';
import Loading from '~/pages/loading';

import EditPatientAppSettings from './EditPatientAppSettings';
import ResendEmailLinkActionBar from './ResendEmailLinkActionBar';
import { capitalizeFirstLetter } from '~/lib/common';

const TOP_LEVEL_ID = 'patient-profile__patient-app-setting-v2';

type AppSettingReadModeProps = {
  chosenName?: string;
  firstName: string;
  hasAuthId?: boolean;
  isAccountActive?: boolean;
  patientId: string;
  showResendInvitation: boolean;
};

const AppSettingReadMode = ({
  chosenName,
  firstName,
  hasAuthId,
  isAccountActive,
  patientId,
  showResendInvitation,
}: AppSettingReadModeProps) => {
  const patientName = capitalizeFirstLetter(
    getPreferredFirstName(chosenName, firstName, false),
  );

  const statusText = getPatientAppSettingReadModeTextWithName(
    patientName,
    hasAuthId,
    isAccountActive,
  );

  return (
    <ChakraBox marginTop="24px">
      {hasAuthId && isAccountActive ? (
        <MaudCardFrame id={`${TOP_LEVEL_ID}__account-status`}>
          <ChakraFlex justifyContent="space-between">
            <Text id={`${TOP_LEVEL_ID}__patient-status-text`}>
              {statusText}
            </Text>
            <ChakraFlex gap="6px">
              <Icon
                color="accent.blue"
                name={FAIconName.laptopMobileRegular}
                size="md"
              />
              <Text color="accent.blue" id={`${TOP_LEVEL_ID}__patient-text`}>
                Patient
              </Text>
            </ChakraFlex>
          </ChakraFlex>
          <ResendEmailLinkActionBar
            id={`${TOP_LEVEL_ID}__view-resend-confirmation-email`}
            patientId={patientId}
            patientName={patientName}
            showResendInvitation={showResendInvitation}
          />
        </MaudCardFrame>
      ) : (
        <EmptySectionCard
          caption={statusText}
          id={`${TOP_LEVEL_ID}__empty-account-status`}
        />
      )}
    </ChakraBox>
  );
};

interface PatientAppSettingsProps {
  hasPatientDashboardPermission: boolean;
  setPatientBasicDetails: (data: Patient) => void;
  patientBasicDetails: Patient;
}

const PatientAppSettings = ({
  hasPatientDashboardPermission,
  setPatientBasicDetails,
  patientBasicDetails,
}: PatientAppSettingsProps) => {
  const [isPatientAppSettingEditMode, setIsPatientAppSettingEditMode] =
    useState<boolean>(false);

  const dismissEditMode = () => setIsPatientAppSettingEditMode(false);
  const { trackEvent } = useAnalytics();

  const {
    hasAuthId,
    hasSetPassword,
    isAccountActive,
    userId: patientId,
  } = patientBasicDetails ?? {};

  const showResendInvitation = isAccountActive && hasAuthId && !hasSetPassword;

  return (
    <>
      <MaudSectionHeader
        id={`${TOP_LEVEL_ID}__section-header`}
        style="secondary"
        title="Equip App settings"
      >
        {!isPatientAppSettingEditMode && hasPatientDashboardPermission && (
          <FireflyButton
            id={`${TOP_LEVEL_ID}__edit-button`}
            leftIcon={FAIconName.penRegular}
            onClick={() => {
              trackEvent(
                PatientEvents.editEquipAppSettingsProfileTabButtonClicked,
                {
                  Source: AnalyticsSourceValues.PATIENT_TAB,
                },
              );
              setIsPatientAppSettingEditMode(true);
            }}
            variant="secondary"
          >
            Edit
          </FireflyButton>
        )}
      </MaudSectionHeader>

      {patientBasicDetails ? (
        <ChakraBox marginBottom="36px">
          {hasPatientDashboardPermission && isPatientAppSettingEditMode ? (
            <EditPatientAppSettings
              dismissEditMode={dismissEditMode}
              patientBasicDetails={patientBasicDetails}
              setPatientBasicDetails={setPatientBasicDetails}
            />
          ) : (
            <AppSettingReadMode
              {...patientBasicDetails}
              patientId={patientId}
              showResendInvitation={showResendInvitation}
            />
          )}
        </ChakraBox>
      ) : (
        <Loading />
      )}
    </>
  );
};

export default PatientAppSettings;
